import React from "react";
import { compareDate, formatDate } from "../../../service/convert/date";
import { useNavigate } from "react-router-dom";
import { IDocument } from "../../../store/@types/document";
import { useAppDispatch, useAppSelector } from "../../../store";
import { Popconfirm } from "antd";
import { deleteDocument } from "../../../service/api/document";

//icons
import {
  IconEye,
  IconEdit,
  IconTrash,
  IconFileTextFilled,
} from "@tabler/icons-react";

type IFileData = {
  item: IDocument;
};

const FileData: React.FC<IFileData> = ({ item }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ document }) => document.loading);
  const user = useAppSelector(({ auth }) => auth.user);
  const expired = compareDate(item?.expiring_date);

  const canEdit = user?.has_company === true;
  const canDelete = user?.has_company === true;

  const handleEdit = () => {
    if (!canEdit) return;
    navigate(`/dashboard/documents/${item?.id}/edit`);
  };

  const handleDelete = () => {
    if (!canDelete) return;
    deleteDocument(dispatch, item?.id);
  };

  return (
    <div className="group w-full bg-white rounded-lg shadow-sm border border-gray-100/20 hover:border-primary-100 hover:shadow-md transition-all duration-200 mb-6">
      <div className="flex items-center gap-4 p-4">
        <div className="flex-shrink-0">
          <IconFileTextFilled
            size={28}
            className={expired ? "text-[red]" : "text-primary-500"}
          />
        </div>

        <div className="flex-grow min-w-0">
          <h3
            className={`text-base font-medium truncate ${
              expired ? "text-[red]" : "text-gray-900"
            }`}
          >
            {item?.name}
          </h3>

          <div className="flex flex-col lg:flex-row gap-0 lg:gap-6 mt-1 text-sm text-gray-500">
            {item?.issuing_body && (
              <span className="flex items-center">
                <span className="mr-2">Issued:</span>
                {formatDate(new Date(item.issuing_date))}
              </span>
            )}
            {item.expiring_date && (
              <span
                className={`flex items-center ${expired ? "text-[red]" : ""}`}
              >
                <span className="mr-2">Expires:</span>
                {formatDate(new Date(item.expiring_date))}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View button - visible to all authenticated users */}
          <button
            onClick={() => navigate("/dashboard/documents/" + item?.id)}
            className="flex items-center justify-center w-8 h-8 bg-gray-50/10 rounded-lg text-gray-400 
                    hover:text-primary-600 transition-colors duration-200"
            aria-label="View document details"
          >
            <IconEye size={20} />
          </button>

          {/* Edit button - only role 1 */}
          {canEdit && (
            <button
              onClick={handleEdit}
              className="flex items-center justify-center w-8 h-8 bg-gray-50/10 rounded-lg text-gray-400 
                    hover:text-primary-600 transition-colors duration-200"
              aria-label="Edit document"
            >
              <IconEdit size={20} />
            </button>
          )}

          {/* Delete button - only role 1 */}
          {canDelete && (
            <Popconfirm
              title="Delete Document"
              description="Are you sure you want to delete this document? This action cannot be undone."
              onConfirm={handleDelete}
              okButtonProps={{
                loading,
                className: "bg-red-500 hover:bg-red-600 text-white border-none",
              }}
              okText="Delete"
              cancelText="Cancel"
            >
              <button
                className="flex items-center justify-center w-8 h-8 bg-gray-50/10 rounded-lg text-gray-400 
              transition-colors duration-200 hover:bg-red-50"
                aria-label="Delete document"
              >
                <IconTrash size={20} className="text-red-500" />
              </button>
            </Popconfirm>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileData;
